import { CloseIcon, SearchIcon } from "@codat/orchard-ui";
import { Integration } from "api/company";
import classNames from "classnames";
import { useConfig } from "contexts/ConfigContext";
import { CSSProperties, useEffect, useMemo, useRef, useState } from "react";
import { useDebounce } from "use-debounce";

import TextInput from "components/TextInput";

import styles from "./CompanyLink.module.css";
import LinkCompanyContainer from "./LinkCompanyContainer";

export interface LinkCompanySearchContainerProps {
  className?: string;
  integrations: Integration[];
  selectedIntegration?: string;
  onIntegrationSelect: (integrationKey: string) => void;
}

interface CustomStyles extends CSSProperties {
  "--theme-color-primary": string;
  "--theme-color-secondary": string;
}

const LinkCompanySearchContainer: React.FC<LinkCompanySearchContainerProps> = ({
  className,
  integrations,
  selectedIntegration,
  onIntegrationSelect,
}) => {
  const {
    state: { data: config },
  } = useConfig();
  const [searchText, setSearchText] = useState<string>("");
  const [searchValue] = useDebounce(searchText, 200);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);

  useEffect(() => {
    const divElement = scrollRef.current;

    if (divElement) {
      const handleScroll = () => {
        const { scrollTop, scrollHeight, clientHeight } = divElement;
        setIsScrolledToBottom(scrollTop + clientHeight >= scrollHeight);
      };

      divElement.addEventListener("scroll", handleScroll);

      return () => {
        divElement.removeEventListener("scroll", handleScroll);
      };
    }
  }, []);

  const searchedIntegrations = useMemo<Integration[]>(() => {
    if (!searchText || !searchValue) return [];

    return integrations.filter(
      (integration) =>
        integration.name.toLowerCase().includes(searchValue.toLowerCase()) ||
        integration.alternativeNames?.some((a) =>
          a.toLowerCase().includes(searchValue.toLowerCase())
        )
    );
  }, [integrations, searchValue, searchText]);

  const handleSelectedItemChange: React.ChangeEventHandler<HTMLInputElement> = (
    event
  ) => {
    onIntegrationSelect("");
    setSearchText(event.target.value);
  };

  const handleSearchClear = () => {
    onIntegrationSelect("");
    setSearchText("");
  };

  return (
    <div className={className}>
      <TextInput
        id="companySearchInput"
        placeholder="Start typing..."
        onChange={handleSelectedItemChange}
        iconLeft={
          searchText
            ? {
                icon: <CloseIcon />,
                onClick: handleSearchClear,
                label: "Clear search",
              }
            : {
                icon: <SearchIcon />,
              }
        }
        value={searchText}
        data-testid="link-company-search-input"
        label="Search integrations"
        inputClassName={styles.searchInput}
        style={
          {
            "--theme-color-primary": config.colors?.primary
              ? config.colors?.primary
              : "var(--color-ui-action)",
            "--theme-color-secondary": config.colors?.secondary
              ? config.colors?.secondary
              : null,
          } as CustomStyles
        }
        analyticsName="Integration search"
      />
      <div
        className={classNames(styles.integrationsContainer, {
          [styles.fadeout]: !isScrolledToBottom,
        })}
        ref={scrollRef}
      >
        <LinkCompanyContainer
          integrations={searchText ? searchedIntegrations : integrations}
          onIntegrationSelect={onIntegrationSelect}
          selectedIntegration={selectedIntegration}
          clientName={config.clientName}
          link={config.help?.link}
        />
      </div>
    </div>
  );
};

export default LinkCompanySearchContainer;
